import Fireworks from "@fireworks-js/react";
import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {QRCodeSVG} from "@rc-component/qrcode/lib/QRCodeSVG";
import {useEffect, useRef, useState} from "react";

import ApiService from "../../../../services/ApiService";
import s from "./style.module.css";

const CAMPAIGN_ID = "3887da60-080b-4969-8284-472adf6856a2";
const CAMPAIGN_URL = "https://charithon.io/61-flammes-nahariya";
const FLAME_PATH = "M28 1C33 23 10 30 7 47C3 62 10 78 25 83C16 73 18 62 26 55C24 66 30 73 36 77C47 70 51 59 48 48C45 38 39 33 40 25C33 33 32 39 33 44C25 35 25 22 28 1Z";
const amount = value => Math.max(0, Number.isFinite(Number(value)) ? Number(value) : 0);
const donationId = donation => donation?.id || donation?._id;
const currencySymbol = currency => ({eur:"€", gbp:"£", ils:"₪", nis:"₪", usd:"$"}[String(currency || "ils").toLowerCase()] || String(currency).toUpperCase());

function AnimatedTotal({value, paused, reduceMotion, format}) {
    const [displayed, setDisplayed] = useState(value);
    const current = useRef(value);
    useEffect(() => {
        if (paused) return;
        if (reduceMotion) {
            current.current = value;
            setDisplayed(value);
            return;
        }
        const from = current.current;
        const start = performance.now();
        let frame;
        const step = now => {
            const elapsed = Math.min((now - start) / 1250, 1);
            current.current = from + (value - from) * (1 - Math.pow(1 - elapsed, 3));
            setDisplayed(current.current);
            if (elapsed < 1) frame = requestAnimationFrame(step);
        };
        frame = requestAnimationFrame(step);
        return () => cancelAnimationFrame(frame);
    }, [value, paused, reduceMotion]);
    return <>{format(displayed)}</>;
}

// The public feed already applies gala visibility/privacy. Do not announce initial history.
// Failed refreshes retain the last good feed; obsolete requests cannot overwrite it.
function useDonations(total, minimum) {
    const [feed, setFeed] = useState({donations:[], status:"loading"});
    const [queue, setQueue] = useState([]);
    const seen = useRef(new Set());
    const initialized = useRef(false);
    useEffect(() => {
        let active = true;
        let timer;
        const load = async () => {
            try {
                const response = await ApiService.get(`/campagne/donors/${CAMPAIGN_ID}`);
                if (!active) return;
                if (!Array.isArray(response?.data)) throw new Error("Invalid donor feed");
                const eligible = response.data.filter(donation => amount(donation?.montant) >= minimum);
                const fresh = initialized.current ? eligible.filter(donation => donationId(donation) && !seen.current.has(donationId(donation))) : [];
                response.data.forEach(donation => {
                    if (donationId(donation)) seen.current.add(donationId(donation));
                });
                initialized.current = true;
                setFeed({donations:eligible.slice(0, 3), status:"ready"});
                if (fresh.length) setQueue(previous => [...previous, ...fresh.reverse()]);
            } catch (_) {
                if (active) setFeed(previous => ({...previous, status:"error"}));
            } finally {
                if (active) timer = setTimeout(load, 5000);
            }
        };
        load();
        return () => { active = false; clearTimeout(timer); };
    }, [minimum, total]);
    return {...feed, queue, setQueue};
}

function Gala({context, loading, campagneData}) {
    const [scale, setScale] = useState(1);
    const [reduceMotion, setReduceMotion] = useState(false);
    const [hidden, setHidden] = useState(false);
    const [failedLogo, setFailedLogo] = useState(null);
    const [gift, setGift] = useState(null);
    const [cooldown, setCooldown] = useState(false);
    const total = amount(campagneData?.stats?.total?.sum?.total);
    const objective = amount(campagneData?.objectif);
    const primaryCurrency = campagneData?.principalDevise || context?.org?.devise || "ils";
    const symbol = currencySymbol(primaryCurrency);
    const locale = context?.lang?.value || context?.lang || "fr";
    const numberFormat = new Intl.NumberFormat(locale === "gr" ? "el" : locale, {maximumFractionDigits:0});
    const format = value => numberFormat.format(amount(value));
    const progress = objective > 0 ? total / objective * 100 : 0;
    const percentage = new Intl.NumberFormat(locale === "gr" ? "el" : locale, {minimumFractionDigits:1, maximumFractionDigits:1}).format(progress);
    const minimum = amount(campagneData?.screenOptions?.minMontant);
    const {donations, status, queue, setQueue} = useDonations(total, minimum);
    const empty = !loading && status === "ready" && donations.length === 0 && total === 0;
    const goalReached = objective > 0 && total >= objective;
    const scene = gift ? "gift" : goalReached ? "goal" : empty ? "empty" : "collection";
    const campaignLabel = campagneData?.label || "61 Flammes";
    const logo = context?.org?.logo;
    const organisationLabel = context?.org?.name || "Beth Habad Francophone de Nahariya";
    const giftName = gift?.campagneExtraData?.displayName || t`Anonyme`;
    const longGiftName = giftName.length > 26;

    useEffect(() => {
        const fit = () => setScale(Math.min(window.innerWidth / 1920, window.innerHeight / 1080));
        const visibility = () => setHidden(document.hidden);
        const query = window.matchMedia("(prefers-reduced-motion: reduce)");
        const motion = () => setReduceMotion(query.matches);
        const body = document.body;
        const previous = {overflow:body.style.overflow, cursor:body.style.cursor};
        body.style.overflow = "hidden";
        body.style.cursor = "none";
        fit(); motion(); visibility();
        window.addEventListener("resize", fit);
        document.addEventListener("visibilitychange", visibility);
        query.addEventListener?.("change", motion);
        return () => {
            body.style.overflow = previous.overflow;
            body.style.cursor = previous.cursor;
            window.removeEventListener("resize", fit);
            document.removeEventListener("visibilitychange", visibility);
            query.removeEventListener?.("change", motion);
        };
    }, []);

    useEffect(() => {
        if (!gift && !cooldown && !hidden && !loading && queue.length) {
            setGift(queue[0]);
            setQueue(previous => previous.slice(1));
        }
    }, [gift, cooldown, hidden, loading, queue, setQueue, total]);

    useEffect(() => {
        if (!gift || hidden) return;
        const timer = setTimeout(() => {setGift(null); setCooldown(true);}, 6500);
        return () => clearTimeout(timer);
    }, [gift, hidden]);

    useEffect(() => {
        if (!cooldown) return;
        const timer = setTimeout(() => setCooldown(false), 4500);
        return () => clearTimeout(timer);
    }, [cooldown]);

    const donationAmount = donation => <>{format(donation.montant)} <span className={s["donor-currency"]}>{currencySymbol(donation.devise || primaryCurrency)}</span></>;

    return (
        <main className={s.viewport} aria-busy={loading} aria-label={campaignLabel}>
            <div className={s["stage-shell"]} style={{width:1920 * scale, height:1080 * scale}}>
                <div className={s.stage} data-scene={scene} data-motion={reduceMotion ? "off" : "on"} data-tab-hidden={hidden} dir="ltr" style={{transform:`scale(${scale})`}}>
                    <div className={s.scenery} aria-hidden="true">
                        <img className={s["scenery-photo"]} src="/campagne/screen/custom-nahariya-61/backdrop.png" alt="" width="2000" height="1125"/>
                        <div className={s["scenery-veil"]}/><div className={s["scenery-light"]}/><div className={s["scenery-warmth"]}/>
                    </div>
                    <header className={s.masthead}>
                        <div className={s.campaign}>
                            <svg className={s["brand-flame"]} viewBox="0 0 54 86" aria-hidden="true"><path fill="currentColor" d={FLAME_PATH}/></svg>
                            <div className={s.wordmark}><strong>61</strong><span>{campagneData?.config?.donorNameMulti || <Trans>Flammes</Trans>}<small><Trans>Pour Nahariya</Trans></small></span></div>
                        </div>
                        <div className={s.organisation}>
                            <div className={s["organisation-copy"]}><strong>Beth Habad Francophone</strong><Trans>de Nahariya</Trans></div>
                            {logo && failedLogo !== logo && <img src={logo} alt={organisationLabel} width="104" height="104" onError={() => setFailedLogo(logo)}/>}
                        </div>
                    </header>
                    <section className={s.collection} aria-hidden={!['collection', 'empty'].includes(scene)}>
                        <h1 className={s.headline}>{empty ? <Trans>Ensemble, allumons la première flamme.</Trans> : <Trans>Ensemble, faisons grandir notre Beth Habad.</Trans>}</h1>
                        <div className={s.total} style={format(total).length > 9 ? {fontSize:230} : undefined}>
                            <span className={s["total-number"]}>{loading ? "—" : <AnimatedTotal value={total} paused={Boolean(gift)} reduceMotion={reduceMotion} format={format}/>}</span><span className={s.currency}>{symbol}</span>
                        </div>
                        <div className={s["goal-line"]}><span><Trans>collectés sur</Trans></span><strong>{format(objective)} {symbol}</strong><span className={s.pct}>{percentage} %</span></div>
                    </section>
                    <section className={s.recognition} aria-hidden={scene !== "gift"} aria-live="polite">
                        <p className={s.thanks}><Trans>Merci pour votre générosité</Trans></p>
                        <h2 className={`${s["recognition-name"]} ${longGiftName ? s.long : ""}`} dir="auto">{giftName}</h2>
                        <div className={s["recognition-amount"]}>{format(gift?.montant)}<span>{currencySymbol(gift?.devise || primaryCurrency)}</span></div>
                        <p className={s["recognition-note"]}><Trans>Votre engagement fait grandir notre communauté.</Trans></p>
                    </section>
                    <section className={s.achieved} aria-hidden={scene !== "goal"}>
                        <p className={s.thanks}><Trans>Grâce à vous</Trans></p><h2><Trans>Objectif atteint.</Trans></h2>
                        <div className={s["goal-amount"]}>{format(total)}<span>{symbol}</span></div>
                        <p className={s["recognition-note"]}><Trans>61 flammes pour faire rayonner Nahariya. Merci à tous.</Trans></p>
                    </section>
                    <div className={s["meter-area"]}>
                        <div className={s.meter} data-flame-count="61" role="progressbar" aria-label={t`Objectif financier`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.min(progress, 100)} aria-valuetext={`${format(total)} / ${format(objective)} ${symbol}`}>
                            <div className={s["meter-fill"]} style={{width:`${Math.min(progress, 100)}%`}}/>
                        </div>
                        <div className={s["meter-labels"]}>
                            <span className={s["meter-caption"]}>{gift ? <><Trans>Total collecté :</Trans> <strong>{format(total)} {symbol}</strong></> : goalReached ? <Trans>Merci de faire vivre notre communauté.</Trans> : <Trans>Chaque don nous rapproche de l’objectif.</Trans>}</span>
                            <a href={CAMPAIGN_URL} target="_blank" rel="noopener noreferrer"><strong>charithon.io</strong>/61-flammes-nahariya</a>
                        </div>
                    </div>
                    <footer className={s["donor-deck"]}>
                        <section className={s["donor-main"]}>
                            <h2 className={s["donor-heading"]}>{empty ? <Trans>La collecte commence avec vous</Trans> : <Trans>Merci à nos derniers donateurs</Trans>}</h2>
                            {donations.length > 0 ? <div className={s.donors}>
                                {donations.map((donation, index) => {
                                    const name = donation?.campagneExtraData?.displayName || t`Anonyme`;
                                    return <article className={`${s.donor} ${index === 0 ? s.highlight : ""}`} key={donationId(donation) || index}>
                                        <h3 className={`${s["donor-name"]} ${name.length > 25 ? s.long : ""}`} dir="auto" title={name}>{name}</h3>
                                        <div className={s["donor-amount"]} style={format(donation.montant).length > 8 ? {fontSize:50} : undefined}>{donationAmount(donation)}</div>
                                    </article>;
                                })}
                            </div> : <div className={`${s["empty-donors"]} ${s.visible}`} role="status">
                                {status === "loading" ? <Trans>Chargement des dons…</Trans> : status === "error" ? <Trans>Actualisation des dons en cours…</Trans> : empty ? <Trans>Faites le premier geste pour notre Beth Habad.</Trans> : <Trans>Merci de faire vivre notre communauté.</Trans>}
                            </div>}
                        </section>
                        <aside className={s["qr-rail"]}><a href={CAMPAIGN_URL} target="_blank" rel="noopener noreferrer" aria-label={t`Participer à la campagne 61 Flammes`}>
                            <QRCodeSVG value={CAMPAIGN_URL} size={174} level="M" marginSize={4} fgColor="#142f5b" bgColor="#ffffff"/>
                            <span><Trans>Je participe</Trans></span>
                        </a></aside>
                    </footer>
                    {!loading && campagneData?.screenOptions?.forceCelebration && !reduceMotion && !hidden && <Fireworks autostart options={{intensity:24, particles:82, traceLength:1, opacity:.32}} className={s.fireworks}/>}
                </div>
            </div>
        </main>
    );
}

export default function CustomNahariya61(props) {
    // This art direction and its public QR destination belong to this campaign only.
    if (props.campagneData?.id !== CAMPAIGN_ID) return null;
    return <Gala {...props}/>;
}
